import { View, StyleSheet } from "react-native";
import BlankLayout from "../layouts/BlankLayout";
import LoginFormContainer from "../containers/auth/LoginFormContainer/LoginFormContainer";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { useAuth } from "../hooks/useAuth";
import { NavigationProp, RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { useAppConfig } from "../AppConfigProvider";
import { useEffect, useMemo } from "react";
import type { RootStackParamList } from "../navigation/navigation.types";
import { getDefaultAuthenticatedRoute } from "../utils/authRoutes.utils";
import { UserRoleUI } from "../contexts/AuthContext";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import Loader from "../components/common/status/Loader/Loader";

function LoginView(isLMS = false) {
  const { isLarge } = useResponsiveScreen();
  const { isAuthenticated, isEmailVerified, otpVerifiedForSession, userInfo } = useAuth();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "LoginSuperAdmin">>();
  const { isCompanionMode, isLTI, ltiLaunchUrl } = useAppConfig();

  const isSuperAdminRoute = route.name === "LoginSuperAdmin";
  const shouldRedirectToLms = Boolean(isLTI && !isSuperAdminRoute);
  const redirectTarget = useMemo(() => ltiLaunchUrl ?? null, [ltiLaunchUrl]);

  useEffect(() => {
    if (isAuthenticated && isEmailVerified && otpVerifiedForSession) {
      const redirect = getDefaultAuthenticatedRoute({
        userRole: userInfo?.userRole ?? UserRoleUI.STUDENT,
        isCompanionMode,
      });
      navigation.navigate(redirect);
    }
  }, [isAuthenticated, isEmailVerified, otpVerifiedForSession, navigation, isCompanionMode, userInfo?.userRole]);

  useEffect(() => {
    if (!shouldRedirectToLms || !redirectTarget) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const timeout = window.setTimeout(() => {
      window.location.href = redirectTarget;
    }, 1500);

    return () => window.clearTimeout(timeout);
  }, [redirectTarget, shouldRedirectToLms]);

  if (shouldRedirectToLms) {
    return (
      <BlankLayout>
        <View style={{ width: "100%", height: "100%", justifyContent: "center", alignItems: "center" }}>
          <View style={styles.redirectContainer}>
            <CustomText
              role="header"
              text="This tenant uses LMS sign-in."
              size="l"
              textType="display"
              weight="bold"
              useTranslationText={false}
            />
            <CustomSpacing size="s" />
            <CustomText
              role="header"
              text={
                redirectTarget
                  ? "We will redirect you back to your LMS to relaunch Axio."
                  : "Return to your LMS to relaunch Axio."
              }
              size="m"
              weight="regular"
              useTranslationText={false}
            />
            <CustomSpacing size="l" />
            {redirectTarget ? <Loader /> : null}
          </View>
        </View>
      </BlankLayout>
    );
  }

  return isLarge ? (
    <BlankLayout>
      <View style={[styles.webContainer, { width: !isLMS ? "40%" : "100%" }]}>
        <LoginFormContainer isSuperAdminRoute={isSuperAdminRoute} />
      </View>
    </BlankLayout>
  ) : (
    <BlankLayout>
      <View style={styles.mobileContainer}>
        <View style={{ padding: 15 }}>
          <LoginFormContainer isSuperAdminRoute={isSuperAdminRoute} />
        </View>
      </View>
    </BlankLayout>
  );
}

const styles = StyleSheet.create({
  webContainer: {
    justifyContent: "center",
    alignItems: "center",
    padding: 30,
  },
  redirectContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    maxWidth: 520,
    alignSelf: "center",
  },
  mobileContainer: {
    flex: 1,
    justifyContent: "flex-start",
    alignItems: "center",

    width: "100%",
  },
});

export default LoginView;
